import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/widgets-datagrid';
import * as _ from 'underscore';

import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { RightAlignedCell, RightAlignedHeader } from '../../common/react/components/dataGrid/StyledCellWrapper';
import { ProfilePicture } from '../../common/react/components/ProfilePicture';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { IGroupInfo } from '../../ts/models/IGroupInfo';
import { SiteRoleOptionsResource } from '../../ts/resources/SiteRoleOptions';
import { IActionButton } from '../../ts/services/ActionPanelService';
import { ProfilePictureStyles } from './styles/ProfilePictureStyles';

export module GroupGrid {

  export function columns(
      translate: IReactTranslateService,
      scope: CommonDataGridColumns.ISelectColumnScope<IGroupInfo>,
      showMinimumSiteRole: boolean,
      isServerDomainFamilyActiveDirectory: boolean,
      actions: IActionButton<IGroupInfo, {}>[],
      getActionTarget: () => IActionTarget<IGroupInfo, {}>,
      siteUrls: SiteUrls,
      siteRoleOptionsResource: SiteRoleOptionsResource
    )
    : DataGrid.Column[] {

    const compositeCellElements: DataGridCells.CellFormatterWithStyle[] = [{
      cellFormatter: DataGridCells.ProfilePictureCell((row: DataGrid.Row) => ({
        profileDisplayName: row.data.name,
        imageUrl: '',
        divShape: ProfilePicture.Shape.Square,
        isAllUsersGroup: row.data.isAllUsersGroup
      })),
      cellChildStyle: ProfilePictureStyles.Layout.UserNameWithProfilePictureCellChildStyle
    }, {
      cellFormatter: DataGridCells.Link((row: DataGrid.Row) => ({
        text: row.data.name,
        url: siteUrls.groupUrl(row.data.id)
      })),
      cellChildStyle: ProfilePictureStyles.Layout.UserNameWithProfilePictureCellChildDisplayNameStyle
    }];

    const GroupNameColumnWithProfileIconsCellFormatter = DataGridCells.CompositeCellWithCustomChildStyles(
      compositeCellElements,
      ProfilePictureStyles.Layout.UserNameWithProfilePictureCellWrapperStyle
    );

    const isRowHeightTaller: boolean = true; // true for user/groups due to profile pic
    const groupNameColumn: DataGrid.Column = CommonDataGridColumns.withExtraSpace(CommonDataGridColumns.withActions({
        name: 'name-col',
        dataKey: 'name',
        headerText: translate('column_name'),
        direction: 'asc',
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: GroupNameColumnWithProfileIconsCellFormatter
      },
      {
        actions,
        translate,
        getActionTarget: getActionTarget,
      },
      scope, isRowHeightTaller));

    const domainNameColumn = {
      name: 'domain-name-col',
      dataKey: 'domainName',
      direction: 'asc',
      headerFormatter: SortableHeader.Formatter(),
      headerText: translate('column_domainName')
    };

    const userCountColumn = {
      name: 'user-count-col',
      dataKey: 'userCount',
      headerText: translate('column_userCount'),
      direction: 'asc',
      headerFormatter: RightAlignedHeader(SortableHeader.Formatter()),
      cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('userCount')),
    };

    const minSiteRoleCol = {
      name: 'min-site-role-col',
      dataKey: 'minimumSiteRole',
      headerText: translate('column_minimumSiteRole'),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.SiteRoleCell('minimumSiteRole', translate, siteRoleOptionsResource),
      direction: 'asc'
    };

    return _.compact([
      CommonDataGridColumns.selected(scope),
      groupNameColumn,
      isServerDomainFamilyActiveDirectory && domainNameColumn,
      userCountColumn,
      showMinimumSiteRole && minSiteRoleCol
    ]);
  }
}
